import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import striptags from 'striptags';
import AOVVerticalCards from '../../../components/AOVVerticalCards';
import styles from './FlatPercentOff.module.scss';
import AOVModalFooter from '../AOVModalFooter';
import RichTextDisplay from '../../../components/RichTextDisplay/RichTextDisplay';
import {
  getVariantsWithPercentDiscount,
  onChangeSKUSelectedForIndex,
} from '../../../utils';
import { setPDPEstimatedTotal } from '../../../slices/pdp';
import messages from './messages';
import { AovLineItem, AovVariant, FlatPercentOffProps } from './types';
import { CartInStore } from '../../../slices/cart/types';
import { useThunkDispatch } from '../../../hooks/useThunkDispatch/useThunkDispatch';

const FlatPercentOff = ({
  title,
  description,
  summaryDisclaimer,
  items,
  onCloseModal,
  children,
  hasPromotionalBanner,
  mainProductAttributes,
}: FlatPercentOffProps) => {
  const [selectedSKUsForProducts, setSelectedSKUsForProducts] = useState<
    Record<string, string[]>
  >({});
  const [quantitiesForProducts, setQuantitiesForProducts] = useState<
    Record<string, number>
  >({});
  const [lineItems, setLineItems] = useState<
    | AovLineItem[]
    | {
        sku: string;
        quantity: number;
        productName: string | undefined;
        discountedPrice?: { centAmount: number; currencyCode: string };
        price?: { centAmount: number; currencyCode: string };
      }[]
  >([]);
  const [filteredItems, setFilteredItems] = useState(items);

  const { locale, formatMessage } = useIntl();
  const dispatch = useThunkDispatch();

  const { recentlyAddedItems } = useSelector(
    (state: { cart: CartInStore }) => state.cart,
  );

  useEffect(() => {
    if (recentlyAddedItems && recentlyAddedItems?.length > 0) {
      const filteredAovItems = items.filter(
        (aovItem) =>
          !recentlyAddedItems.find(
            (addedItem) => addedItem.productKey === aovItem.productKey,
          ),
      );
      setFilteredItems(filteredAovItems);
    }
  }, [items, recentlyAddedItems]);

  useEffect(() => {
    const newLineItems: {
      sku: string;
      quantity: number;
      productName: string | undefined;
      discountedPrice?: { centAmount: number; currencyCode: string };
      price?: { centAmount: number; currencyCode: string };
    }[] = Object.keys(selectedSKUsForProducts)
      .map((productId: string) => {
        const currentProduct: AovLineItem | undefined = items.find(
          (item) => item.productId === productId,
        );
        const quantity = quantitiesForProducts[productId] || 1;

        const selectedSKUsWithProductInfo = selectedSKUsForProducts[
          productId
        ].map((selectedVariantSKU: string, index: number) => {
          const currentVariant = currentProduct?.variants.find(
            (variant) => variant.sku === selectedVariantSKU,
          );

          const moreThanOneSelected =
            selectedSKUsForProducts[productId].filter(
              (selectedSkuForCurrentProduct: string) =>
                selectedSkuForCurrentProduct === selectedVariantSKU,
            )?.length > 1;
          // if same variant was added 2 times
          // calculate the discounted price
          let currentVariantWithDiscountedPrice: AovVariant | undefined;

          if (moreThanOneSelected && currentVariant) {
            const variantsWithDiscount: AovVariant[] =
              index === 0
                ? (getVariantsWithPercentDiscount(
                    [currentVariant],
                    currentProduct?.secondItemDiscount
                      ?.percentageFirstProduct || 0,
                  ) as AovVariant[])
                : (getVariantsWithPercentDiscount(
                    [currentVariant],
                    currentProduct?.secondItemDiscount?.percentage || 0,
                  ) as AovVariant[]);
            [currentVariantWithDiscountedPrice] = variantsWithDiscount;
          } else {
            currentVariantWithDiscountedPrice = currentVariant;
          }

          return {
            sku: selectedVariantSKU,
            quantity,
            productName: currentProduct?.productName?.[locale],
            discountedPrice: currentVariantWithDiscountedPrice?.price,
            price: currentVariantWithDiscountedPrice?.discount
              ? {
                  ...currentVariantWithDiscountedPrice?.price,
                  centAmount:
                    (currentVariantWithDiscountedPrice?.price?.centAmount ??
                      0) +
                    (currentVariantWithDiscountedPrice?.discount?.centAmount ??
                      0),
                }
              : currentVariantWithDiscountedPrice?.price,
          };
        });

        return selectedSKUsWithProductInfo;
      })
      ?.reduce((acc, curr) => [...acc, ...curr], []);

    setLineItems(newLineItems);
  }, [selectedSKUsForProducts, quantitiesForProducts, items, locale]);

  useEffect(() => {
    const getAccumulatedTotals = (selectedVariantsList: AovLineItem[]) =>
      selectedVariantsList.reduce(
        (accPrice, currVariant) => {
          const quantity = currVariant.quantity || 1;

          if (currVariant.discountedPrice) {
            return {
              ...currVariant.discountedPrice,
              centAmount:
                accPrice.centAmount +
                currVariant.discountedPrice.centAmount * quantity,
            };
          }

          return {
            ...currVariant.price,
            centAmount:
              accPrice.centAmount + currVariant.price.centAmount * quantity,
          };
        },
        {
          centAmount: 0,
          fractionDigits: 2,
        },
      );
    const accumulatedTotals = getAccumulatedTotals(lineItems as AovLineItem[]);
    if (accumulatedTotals?.centAmount) {
      dispatch(
        setPDPEstimatedTotal({
          totalPrice: accumulatedTotals,
        }),
      );
    } else {
      dispatch(
        setPDPEstimatedTotal({
          totalPrice: {
            centAmount: 0,
          },
        }),
      );
    }
  }, [dispatch, lineItems]);

  const onChangeSKUSelectedForIndexCallback = useCallback(
    ({
      sku,
      index,
      productId,
    }: {
      sku: string | undefined;
      index: number;
      productId: string;
    }) => {
      onChangeSKUSelectedForIndex({
        sku,
        index,
        productId,
        selectedSKUsForProducts,
        setSelectedSKUsForProducts,
      });
    },
    [selectedSKUsForProducts],
  );

  const handleQuantityIncrement = useCallback((productId: string) => {
    setQuantitiesForProducts((prev: Record<string, number>) => ({
      ...prev,
      [productId]: (prev[productId] || 1) + 1,
    }));
  }, []);

  const handleQuantityDecrement = useCallback((productId: string) => {
    setQuantitiesForProducts((prev: Record<string, number>) => ({
      ...prev,
      [productId]: Math.max((prev[productId] || 1) - 1, 1),
    }));
  }, []);

  return (
    <div className={styles['flat-rate-container']}>
      <div className={styles['flat-rate-header']}>
        <div className={styles['flat-rate-title-container']}>
          <div className={styles.title}>{title[locale]}</div>
          {description?.[locale] && striptags(description[locale]).trim() && (
            <div className={styles['flat-rate-description']}>
              <RichTextDisplay content={description[locale]} />
            </div>
          )}
        </div>
        <button
          aria-label={formatMessage(messages.closeModal)}
          type='button'
          onClick={onCloseModal}
          data-testid='btn__close__modal'
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onCloseModal();
            }
          }}
          id='button1'
        >
          <div className={styles['close-button']} />
        </button>
      </div>
      {/* BMSM block */}
      {hasPromotionalBanner && (
        <div className={styles['bmsm-block']}>{children}</div>
      )}
      <AOVVerticalCards
        aovType='flatRate'
        filteredItems={filteredItems}
        onChangeSKUSelectedForIndex={onChangeSKUSelectedForIndexCallback}
        selectedSKUsForProducts={selectedSKUsForProducts}
        quantitiesForProducts={quantitiesForProducts}
        onQuantityIncrement={handleQuantityIncrement}
        onQuantityDecrement={handleQuantityDecrement}
        mainProductAttributes={mainProductAttributes}
      />
      {summaryDisclaimer?.[locale]?.trim() && (
        <div className={styles['flat-rate-summary-disclaimer']}>
          {summaryDisclaimer[locale]}
        </div>
      )}
      <AOVModalFooter lineItems={lineItems} />
    </div>
  );
};

export default FlatPercentOff;
